import { useState } from "react";
import Input from "../../../components/Input/Input";
import MultipleSelect from "../../../components/MultipleSelect/MultipleSelect";
import { TypeEducationalHubFilters } from "../../../types/EducationalHub";
import { TypeUserData } from "../../../types/TypeUsers";
import s from "./HubManagement.module.css";
import { getEndOfSelectedDate } from "./hubManagementUtils";
import UserSearchSelect from "./UserSearchSelect";

type AppliedHubFilters = Omit<TypeEducationalHubFilters, "page" | "limit">;

type HubFiltersProps = {
  onApply: (filters: AppliedHubFilters) => void;
};

const defaultFilters = {
  search: "",
  hubId: "",
  owner: null as TypeUserData | null,
  teamUser: null as TypeUserData | null,
  isApproved: "",
  isApproveRequested: "",
  isActive: "",
  paymentOverdue: "",
  deleted: "",
  createdFrom: "",
  createdTo: "",
  approvedFrom: "",
  approvedTo: "",
};

const booleanOptions = [
  { value: "", lable: "Any" },
  { value: "true", lable: "Yes" },
  { value: "false", lable: "No" },
];

const toBoolean = (value: string): boolean | undefined => {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};

const HubFilters = ({ onApply }: HubFiltersProps) => {
  const [filters, setFilters] = useState(defaultFilters);

  const handleChange = (
    field: keyof typeof defaultFilters,
    value: string | TypeUserData | null,
  ) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    const result: AppliedHubFilters = {};

    if (filters.search.trim()) result.search = filters.search.trim();
    if (filters.hubId.trim()) result.hubId = filters.hubId.trim();
    if (filters.owner?._id) result.ownerId = filters.owner._id;
    if (filters.teamUser?._id) result.teamUserId = filters.teamUser._id;

    const approved = toBoolean(filters.isApproved);
    const requested = toBoolean(filters.isApproveRequested);
    const active = toBoolean(filters.isActive);
    const overdue = toBoolean(filters.paymentOverdue);
    const deleted = toBoolean(filters.deleted);

    if (approved !== undefined) result.isApproved = approved;
    if (requested !== undefined) result.isApproveRequested = requested;
    if (active !== undefined) result.isActive = active;
    if (overdue !== undefined) result.paymentOverdue = overdue;
    if (deleted !== undefined) result.deleted = deleted;

    if (filters.createdFrom || filters.createdTo) {
      result.createdAt = {
        from: filters.createdFrom ? new Date(filters.createdFrom) : undefined,
        to: filters.createdTo
          ? getEndOfSelectedDate(filters.createdTo)
          : undefined,
      };
    }

    if (filters.approvedFrom || filters.approvedTo) {
      result.approvedAt = {
        from: filters.approvedFrom ? new Date(filters.approvedFrom) : undefined,
        to: filters.approvedTo
          ? getEndOfSelectedDate(filters.approvedTo)
          : undefined,
      };
    }

    onApply(result);
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    onApply({});
  };

  return (
    <div className={s.filtersContainer}>
      <div className={`${s.filterGroup} ${s.filterGroupWide}`}>
        <Input
          inputValue={filters.search}
          onChangeInput={(value) => handleChange("search", value)}
          lable="Search"
          placeholder="Title, short ID, subdomain, owner..."
          required={false}
          isSearchinput
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 44px 10px 20px"
        />
      </div>
      <div className={`${s.filterGroup} ${s.filterGroupWide}`}>
        <Input
          inputValue={filters.hubId}
          onChangeInput={(value) => handleChange("hubId", value)}
          lable="Hub ID"
          placeholder="Search by hub ID..."
          required={false}
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 20px"
        />
      </div>
      <div className={`${s.filterGroup} ${s.filterGroupWide}`}>
        <UserSearchSelect
          label="Owner"
          selectedUser={filters.owner}
          onSelect={(user) => handleChange("owner", user)}
          onClear={() => handleChange("owner", null)}
        />
      </div>
      <div className={`${s.filterGroup} ${s.filterGroupWide}`}>
        <UserSearchSelect
          label="Team user"
          selectedUser={filters.teamUser}
          onSelect={(user) => handleChange("teamUser", user)}
          onClear={() => handleChange("teamUser", null)}
        />
      </div>
      <div className={s.filterGroup}>
        <MultipleSelect
          data={booleanOptions}
          setValue={(value) => handleChange("isApproved", value as string)}
          value={filters.isApproved}
          multiplie={false}
          lable="Approved"
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 14px"
          disableSortData
        />
      </div>
      <div className={s.filterGroup}>
        <MultipleSelect
          data={booleanOptions}
          setValue={(value) =>
            handleChange("isApproveRequested", value as string)
          }
          value={filters.isApproveRequested}
          multiplie={false}
          lable="Requested"
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 14px"
          disableSortData
        />
      </div>
      <div className={s.filterGroup}>
        <MultipleSelect
          data={booleanOptions}
          setValue={(value) => handleChange("isActive", value as string)}
          value={filters.isActive}
          multiplie={false}
          lable="Active"
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 14px"
          disableSortData
        />
      </div>
      <div className={s.filterGroup}>
        <MultipleSelect
          data={booleanOptions}
          setValue={(value) => handleChange("paymentOverdue", value as string)}
          value={filters.paymentOverdue}
          multiplie={false}
          lable="Payment overdue"
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 14px"
          disableSortData
        />
      </div>
      <div className={s.filterGroup}>
        <MultipleSelect
          data={booleanOptions}
          setValue={(value) => handleChange("deleted", value as string)}
          value={filters.deleted}
          multiplie={false}
          lable="Deleted"
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 14px"
          disableSortData
        />
      </div>
      <div className={s.dateFilterRow}>
        <div className={s.filterGroup}>
          <Input
            inputValue={filters.createdFrom}
            onChangeInput={(value) => handleChange("createdFrom", value)}
            lable="Created from"
            type="date"
            required={false}
            bgColor="rgba(255, 255, 255, 0.06)"
            border="1px solid rgba(255, 255, 255, 0.12)"
            padding="10px 20px"
          />
        </div>
        <div className={s.filterGroup}>
          <Input
            inputValue={filters.createdTo}
            onChangeInput={(value) => handleChange("createdTo", value)}
            lable="Created to"
            type="date"
            required={false}
            bgColor="rgba(255, 255, 255, 0.06)"
            border="1px solid rgba(255, 255, 255, 0.12)"
            padding="10px 20px"
          />
        </div>
      </div>
      <div className={s.dateFilterRow}>
        <div className={s.filterGroup}>
          <Input
            inputValue={filters.approvedFrom}
            onChangeInput={(value) => handleChange("approvedFrom", value)}
            lable="Approved from"
            type="date"
            required={false}
            bgColor="rgba(255, 255, 255, 0.06)"
            border="1px solid rgba(255, 255, 255, 0.12)"
            padding="10px 20px"
          />
        </div>
        <div className={s.filterGroup}>
          <Input
            inputValue={filters.approvedTo}
            onChangeInput={(value) => handleChange("approvedTo", value)}
            lable="Approved to"
            type="date"
            required={false}
            bgColor="rgba(255, 255, 255, 0.06)"
            border="1px solid rgba(255, 255, 255, 0.12)"
            padding="10px 20px"
          />
        </div>
      </div>

      <div className={s.filterActions}>
        <button className={s.applyBtn} type="button" onClick={handleApply}>
          Apply
        </button>
        <button className={s.resetBtn} type="button" onClick={handleReset}>
          Reset
        </button>
      </div>
    </div>
  );
};

export default HubFilters;
